import { useEffect, useRef, useState, type MouseEvent, type SyntheticEvent } from 'react';
import type { DiagramDocument, LayoutMode } from '../core/types';
import { exportJpeg, exportJson, exportMermaidFile, exportPng, exportSvg, printDiagram } from '../services/export/exporters';
import { exportPlantUml } from '../services/interchange/plantuml';
import { exportDot } from '../services/interchange/dot';
import { exportBpmnXml } from '../services/interchange/bpmnXml';
import { exportCloudCsv } from '../services/import/cloudCsv';
import { downloadBlob } from '../services/export/download';
import { openNativeDocument } from '../core/contract';
import { recoverDocument } from '../core/documentRecovery';
import { Icon, Symbols } from './icons';
import type { LibraryTab } from './ShapePalette';

interface Props {
  readOnly?: boolean;
  document: DiagramDocument;
  canUndo: boolean;
  canRedo: boolean;
  saveStatus: 'saved' | 'saving' | 'error';
  onSave: () => void;
  connectionMode: boolean;
  selectedCount: number;
  selectedEdgeCount: number;
  selectedLocked: boolean;
  canUngroup: boolean;
  canBundleEdges: boolean;
  canUnbundleEdges: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onNew: () => void;
  onProjects: () => void;
  onLayers: () => void;
  onValidate: () => void;
  onCommands: () => void;
  onSearch: () => void;
  onHistory: () => void;
  onHelp: () => void;
  onServer: () => void;
  onComments: () => void;
  onImport: (document: DiagramDocument) => void;
  onImportOrgCsv: (text: string) => void;
  onImportSql: (text: string) => void;
  onImportNetworkCsv: (text: string) => void;
  onImportMermaid: (text: string) => void;
  onImportDataCsv: (text: string) => void;
  onImportPlantUml: (text: string) => void;
  onImportDot: (text: string) => void;
  onImportBpmnXml: (text: string) => void;
  onImportCloudCsv: (text: string) => void;
  onExportWorkspace: () => void;
  onImportWorkspace: (text: string) => void;
  onToggleConnection: () => void;
  onArrange: (direction: LayoutMode) => void;
  onAlign: (mode: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom') => void;
  onDistribute: (axis: 'horizontal' | 'vertical') => void;
  onMatchSize: (mode: 'width' | 'height' | 'both') => void;
  onGroup: () => void;
  onUngroup: () => void;
  onConnectSelection: () => void;
  onSelectConnectedEdges: () => void;
  onBundleEdges: () => void;
  onUnbundleEdges: () => void;
  onToggleLock: () => void;
  onLayer: (direction: 'front' | 'back') => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onFit: () => void;
  onToggleLibrary: (tab?: LibraryTab) => void;
  onToggleProperties: () => void;
  onShare: () => void;
  onHome: () => void;
  onNotifications: () => void;
  unreadNotifications: number;
  onPresentation: () => void;
  userDisplayName: string | null;
  userEmail: string | null;
  userRole: string | null;
  organizationRole: string | null;
  organizationName: string | null;
  planCode: string | null;
  planStatus: string | null;
  onAccount: () => void;
  onMaster: () => void;
  onLogout: () => void;
}

export function TopBar(props: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const orgCsvRef = useRef<HTMLInputElement>(null);
  const sqlRef = useRef<HTMLInputElement>(null);
  const networkCsvRef = useRef<HTMLInputElement>(null);
  const mermaidRef = useRef<HTMLInputElement>(null);
  const dataCsvRef = useRef<HTMLInputElement>(null);
  const plantUmlRef = useRef<HTMLInputElement>(null);
  const dotRef = useRef<HTMLInputElement>(null);
  const bpmnXmlRef = useRef<HTMLInputElement>(null);
  const cloudCsvRef = useRef<HTMLInputElement>(null);
  const workspaceBackupRef = useRef<HTMLInputElement>(null);
  const accountRef = useRef<HTMLDivElement>(null);
  const menuStripRef = useRef<HTMLElement>(null);
  const [accountOpen, setAccountOpen] = useState(false);

  function closeTopMenus(except?: HTMLDetailsElement) {
    menuStripRef.current?.querySelectorAll<HTMLDetailsElement>('details.top-menu[open]').forEach((item) => { if (item !== except) item.open = false; });
  }

  function handleMenuToggle(event: SyntheticEvent<HTMLDetailsElement>) {
    const current = event.currentTarget;
    if (!current.open) return;
    setAccountOpen(false);
    closeTopMenus(current);
    window.requestAnimationFrame(() => {
      const popover = current.querySelector<HTMLElement>('.top-menu-popover');
      if (!popover) return;
      popover.style.transform = '';
      const rect = popover.getBoundingClientRect();
      const margin = 10;
      let shiftX = 0;
      if (rect.right > window.innerWidth - margin) shiftX -= rect.right - (window.innerWidth - margin);
      if (rect.left + shiftX < margin) shiftX += margin - (rect.left + shiftX);
      popover.style.transform = shiftX ? `translateX(${Math.round(shiftX)}px)` : '';
      const after = popover.getBoundingClientRect();
      if (after.bottom > window.innerHeight - margin) {
        popover.style.maxHeight = `${Math.max(180, window.innerHeight - after.top - margin)}px`;
        popover.style.overflowY = 'auto';
      }
    });
  }

  function handleMenuClick(event: MouseEvent<HTMLElement>) {
    const target = event.target as HTMLElement;
    const button = target.closest('button');
    if (!button) return;
    const details = button.closest('details.top-menu') as HTMLDetailsElement | null;
    if (details) details.open = false;
  }

  useEffect(() => {
    if (!accountOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (accountRef.current && !accountRef.current.contains(event.target as Node)) setAccountOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setAccountOpen(false);
    };
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [accountOpen]);

  const isPlatformMaster = props.userRole === 'master';
  const canManageOrganization = ['owner','admin'].includes(props.organizationRole ?? '');
  const accountRoleLabel = isPlatformMaster ? 'Master OWDEX' : props.organizationRole === 'owner' ? 'Responsável da empresa' : props.organizationRole === 'admin' ? 'Administrador da empresa' : 'Usuário';
  const planLabel = props.planCode ? props.planCode.charAt(0).toUpperCase() + props.planCode.slice(1) : 'Plano não informado';

  async function handleImport(file?: File) {
    if (!file) return;
    try {
      if (file.size > 25 * 1024 * 1024) throw new Error('O arquivo excede o limite de 25 MB para importação local.');
      const parsed = JSON.parse(await file.text()) as unknown;
      try {
        const opened = openNativeDocument(parsed);
        props.onImport(opened.document);
        if (opened.warnings.length) window.setTimeout(() => window.alert(opened.warnings.join('\n')), 0);
        return;
      } catch (strictError) {
        const recovery = recoverDocument(parsed);
        if (!recovery.ok || !recovery.document) throw strictError;
        const summary = recovery.changes.slice(0, 8).map((item) => `• ${item}`).join('\n');
        const suffix = recovery.changes.length > 8 ? `\n• +${recovery.changes.length - 8} ajuste(s)` : '';
        const accepted = window.confirm(`O arquivo possui inconsistências recuperáveis. O OWDEX Draw pode criar uma versão reparada sem alterar o arquivo original.\n\n${summary}${suffix}\n\nImportar a versão recuperada?`);
        if (!accepted) return;
        props.onImport(recovery.document);
      }
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Não foi possível importar o arquivo.');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  }


  async function handleOrgCsv(file?: File) {
    if (!file) return;
    try {
      if (file.size > 5 * 1024 * 1024) throw new Error('O CSV excede o limite de 5 MB para importação local.');
      props.onImportOrgCsv(await file.text());
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Não foi possível importar o organograma.');
    } finally {
      if (orgCsvRef.current) orgCsvRef.current.value = '';
    }
  }

  async function handleSql(file?: File) {
    if (!file) return;
    try {
      if (file.size > 5 * 1024 * 1024) throw new Error('O SQL excede o limite de 5 MB para importação local.');
      props.onImportSql(await file.text());
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Não foi possível importar o esquema SQL.');
    } finally {
      if (sqlRef.current) sqlRef.current.value = '';
    }
  }

  async function handleNetworkCsv(file?: File) {
    if (!file) return;
    try {
      if (file.size > 5 * 1024 * 1024) throw new Error('O CSV de rede excede o limite de 5 MB para importação local.');
      props.onImportNetworkCsv(await file.text());
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Não foi possível importar o inventário de rede.');
    } finally {
      if (networkCsvRef.current) networkCsvRef.current.value = '';
    }
  }

  async function handleMermaid(file?: File) {
    if (!file) return;
    try {
      if (file.size > 2 * 1024 * 1024) throw new Error('O Mermaid excede o limite de 2 MB para importação local.');
      props.onImportMermaid(await file.text());
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Não foi possível importar o Mermaid.');
    } finally {
      if (mermaidRef.current) mermaidRef.current.value = '';
    }
  }

  async function handleDataCsv(file?: File) {
    if (!file) return;
    try {
      if (file.size > 5 * 1024 * 1024) throw new Error('O CSV de dados excede o limite de 5 MB para importação local.');
      props.onImportDataCsv(await file.text());
    } catch (error) { window.alert(error instanceof Error ? error.message : 'Não foi possível vincular os dados.'); }
    finally { if (dataCsvRef.current) dataCsvRef.current.value = ''; }
  }

  async function handlePlantUml(file?: File) {
    if (!file) return;
    try { if (file.size > 2 * 1024 * 1024) throw new Error('O PlantUML excede o limite de 2 MB.'); props.onImportPlantUml(await file.text()); }
    catch (error) { window.alert(error instanceof Error ? error.message : 'Não foi possível importar o PlantUML.'); }
    finally { if (plantUmlRef.current) plantUmlRef.current.value = ''; }
  }

  async function handleDot(file?: File) {
    if (!file) return;
    try { if (file.size > 2 * 1024 * 1024) throw new Error('O DOT excede o limite de 2 MB.'); props.onImportDot(await file.text()); }
    catch (error) { window.alert(error instanceof Error ? error.message : 'Não foi possível importar Graphviz DOT.'); }
    finally { if (dotRef.current) dotRef.current.value = ''; }
  }

  async function handleBpmnXml(file?: File) {
    if (!file) return;
    try { if (file.size > 5 * 1024 * 1024) throw new Error('O BPMN XML excede o limite de 5 MB.'); props.onImportBpmnXml(await file.text()); }
    catch (error) { window.alert(error instanceof Error ? error.message : 'Não foi possível importar BPMN XML.'); }
    finally { if (bpmnXmlRef.current) bpmnXmlRef.current.value = ''; }
  }

  async function handleCloudCsv(file?: File) {
    if (!file) return;
    try { if (file.size > 5 * 1024 * 1024) throw new Error('O CSV Cloud excede o limite de 5 MB.'); props.onImportCloudCsv(await file.text()); }
    catch (error) { window.alert(error instanceof Error ? error.message : 'Não foi possível importar o inventário Cloud.'); }
    finally { if (cloudCsvRef.current) cloudCsvRef.current.value = ''; }
  }

  async function handleWorkspaceBackup(file?: File) {
    if (!file) return;
    try { if (file.size > 50 * 1024 * 1024) throw new Error('O backup excede o limite de 50 MB.'); props.onImportWorkspace(await file.text()); }
    catch (error) { window.alert(error instanceof Error ? error.message : 'Não foi possível restaurar o backup.'); }
    finally { if (workspaceBackupRef.current) workspaceBackupRef.current.value = ''; }
  }

  return (
    <header className="topbar" id="editor-topbar" tabIndex={-1}>
      <button type="button" className="brand-row brand-home-button" onClick={props.onHome} title="Início / projetos">
        <img className="brand-mark-image" src="/brand/owdex-draw-mark.png" alt="" aria-hidden="true" />
        <span className="brand-copy"><strong>OWDEX Draw</strong><span>1.0 RC3{props.readOnly ? ' • somente leitura' : ''}</span></span>
      </button>

      <nav ref={menuStripRef} className="menu-strip" aria-label="Menu principal" onClick={handleMenuClick}>
        <details className="top-menu" onToggle={handleMenuToggle}><summary>Arquivo</summary><div className="top-menu-popover">
          <button type="button" onClick={props.onNew}>Novo diagrama <kbd>Ctrl+N</kbd></button>
          <button type="button" onClick={props.onProjects}>Meus projetos</button>
          <button type="button" onClick={() => fileRef.current?.click()}><span><Icon>{Symbols.import}</Icon> Importar arquivo ODXD</span></button>
          <span className="menu-separator" />
          <button type="button" onClick={props.onExportWorkspace}>Gerar backup local</button>
          <button type="button" onClick={() => workspaceBackupRef.current?.click()}>Restaurar backup</button>
        </div></details>
        <details className="top-menu" onToggle={handleMenuToggle}><summary>Projeto</summary><div className="top-menu-popover">
          <button type="button" onClick={props.onLayers}>Camadas</button>
          <button type="button" onClick={props.onSearch}>Buscar <kbd>Ctrl+F</kbd></button>
          <button type="button" onClick={props.onHistory}>Histórico local</button>
          <button type="button" onClick={props.onValidate}>Validar diagrama</button>
          <button type="button" onClick={props.onCommands}>Paleta de comandos <kbd>Ctrl+K</kbd></button>
          <button type="button" onClick={props.onPresentation}>Modo apresentação <kbd>F10</kbd></button>
        </div></details>
        <details className="top-menu" onToggle={handleMenuToggle}><summary>Importar dados</summary><div className="top-menu-popover top-menu-grid">
          <button type="button" onClick={() => orgCsvRef.current?.click()}>Organograma CSV</button>
          <button type="button" onClick={() => sqlRef.current?.click()}>SQL → ERD</button>
          <button type="button" onClick={() => networkCsvRef.current?.click()}>Inventário de rede</button>
          <button type="button" onClick={() => cloudCsvRef.current?.click()}>Inventário Cloud</button>
          <button type="button" onClick={() => mermaidRef.current?.click()}>Mermaid</button>
          <button type="button" onClick={() => plantUmlRef.current?.click()}>PlantUML</button>
          <button type="button" onClick={() => dotRef.current?.click()}>Graphviz DOT</button>
          <button type="button" onClick={() => bpmnXmlRef.current?.click()}>BPMN XML</button>
          <button type="button" onClick={() => dataCsvRef.current?.click()} disabled={props.readOnly}>Vincular dados CSV</button>
        </div></details>
        <details className="top-menu" onToggle={handleMenuToggle}><summary>Exportar</summary><div className="top-menu-popover top-menu-grid">
          <button type="button" onClick={() => exportJson(props.document)}>Arquivo ODXD</button>
          <button type="button" onClick={() => { try { exportMermaidFile(props.document); } catch (error) { window.alert(error instanceof Error ? error.message : 'Falha ao exportar Mermaid.'); } }}>Mermaid</button>
          <button type="button" onClick={() => { try { const page = props.document.pages.find((item) => item.id === props.document.activePageId) ?? props.document.pages[0]; downloadBlob(`${props.document.title.replace(/[^a-z0-9_-]+/gi, '_') || 'owdex_draw'}.puml`, new Blob([exportPlantUml(page)], { type: 'text/plain;charset=utf-8' })); } catch (error) { window.alert(error instanceof Error ? error.message : 'Falha ao exportar PlantUML.'); } }}>PlantUML</button>
          <button type="button" onClick={() => { try { const page = props.document.pages.find((item) => item.id === props.document.activePageId) ?? props.document.pages[0]; downloadBlob(`${props.document.title.replace(/[^a-z0-9_-]+/gi, '_') || 'owdex_draw'}.dot`, new Blob([exportDot(page)], { type: 'text/vnd.graphviz;charset=utf-8' })); } catch (error) { window.alert(error instanceof Error ? error.message : 'Falha ao exportar DOT.'); } }}>DOT</button>
          <button type="button" onClick={() => { try { const page = props.document.pages.find((item) => item.id === props.document.activePageId) ?? props.document.pages[0]; downloadBlob(`${props.document.title.replace(/[^a-z0-9_-]+/gi, '_') || 'owdex_draw'}.bpmn`, new Blob([exportBpmnXml(page)], { type: 'application/xml;charset=utf-8' })); } catch (error) { window.alert(error instanceof Error ? error.message : 'Falha ao exportar BPMN XML.'); } }}>BPMN XML</button>
          <button type="button" onClick={() => { try { const page = props.document.pages.find((item) => item.id === props.document.activePageId) ?? props.document.pages[0]; downloadBlob(`${props.document.title.replace(/[^a-z0-9_-]+/gi, '_') || 'owdex_draw'}_cloud.csv`, new Blob([exportCloudCsv(page.nodes, page.edges)], { type: 'text/csv;charset=utf-8' })); } catch (error) { window.alert(error instanceof Error ? error.message : 'Falha ao exportar inventário Cloud.'); } }}>Cloud CSV</button>
          <button type="button" onClick={() => exportSvg(props.document)}>SVG</button>
          <button type="button" onClick={() => void exportPng(props.document).catch((error: unknown) => window.alert(error instanceof Error ? error.message : 'Falha ao exportar PNG.'))}>PNG</button>
          <button type="button" onClick={() => void exportJpeg(props.document).catch((error: unknown) => window.alert(error instanceof Error ? error.message : 'Falha ao exportar JPG.'))}>JPG</button>
          <button type="button" onClick={() => { try { printDiagram(props.document); } catch (error) { window.alert(error instanceof Error ? error.message : 'Falha ao abrir impressão.'); } }}><span><Icon>{Symbols.print}</Icon> Imprimir / PDF</span></button>
        </div></details>
        <button type="button" className="menu-direct-button share-direct" onClick={() => { closeTopMenus(); props.onShare(); }}>Compartilhar</button>
        <button type="button" className="menu-direct-button" onClick={() => { closeTopMenus(); props.onComments(); }}>Comentários</button>
        <button type="button" className="menu-direct-button" onClick={() => { closeTopMenus(); props.onHelp(); }}>Ajuda</button>
      </nav>

      <button type="button" className="top-notification-button" onClick={() => { closeTopMenus(); setAccountOpen(false); props.onNotifications(); }} title="Notificações" aria-label={`Notificações${props.unreadNotifications ? `, ${props.unreadNotifications} não lida(s)` : ''}`}><Icon>{Symbols.bell}</Icon>{props.unreadNotifications > 0 && <span className="notification-badge">{props.unreadNotifications > 99 ? '99+' : props.unreadNotifications}</span>}</button>

      <div className="account-menu-wrap" ref={accountRef}>
        <button
          type="button"
          className={`account-chip ${accountOpen ? 'is-open' : ''}`}
          onClick={() => { closeTopMenus(); setAccountOpen((value) => !value); }}
          title="Conta e organização"
          aria-haspopup="menu"
          aria-expanded={accountOpen}
        >
          <span className="account-avatar">{(props.userDisplayName?.trim()?.[0] ?? props.userEmail?.[0] ?? 'O').toUpperCase()}</span>
          <span>{props.userEmail ? <><strong>{props.userDisplayName?.trim() || props.userEmail}</strong><small>{accountRoleLabel}</small></> : <><strong>Servidor</strong><small>local-first</small></>}</span>
          <span className="account-chevron" aria-hidden="true">⌄</span>
        </button>
        {accountOpen && <div className="account-popover" role="menu" aria-label="Conta e organização">
          <div className="account-popover-head">
            <span className="account-avatar account-avatar-large">{(props.userDisplayName?.trim()?.[0] ?? props.userEmail?.[0] ?? 'O').toUpperCase()}</span>
            <div><strong>{props.userDisplayName?.trim() || props.userEmail || 'Sessão local'}</strong><span>{props.userEmail ?? accountRoleLabel}</span></div>
          </div>
          <div className="account-context-card">
            <div><span>Organização</span><strong>{props.organizationName ?? 'OWDEX'}</strong></div>
            <div><span>Plano</span><strong>{planLabel}</strong><em className={`account-plan-status status-${props.planStatus ?? 'unknown'}`}>{props.planStatus ?? '—'}</em></div>
          </div>
          <div className="account-menu-actions">
            <button type="button" role="menuitem" onClick={() => { setAccountOpen(false); props.onAccount(); }}><span className="account-menu-icon"><Icon>{Symbols.user}</Icon></span><span><strong>Minha conta</strong><small>Perfil, senha e sessões</small></span></button>
            <button type="button" role="menuitem" onClick={() => { setAccountOpen(false); props.onServer(); }}><span className="account-menu-icon"><Icon>{Symbols.cloud}</Icon></span><span><strong>Servidor e sincronização</strong><small>Projetos, revisões e acessos</small></span></button>
            {(isPlatformMaster || canManageOrganization) && <button type="button" role="menuitem" onClick={() => { setAccountOpen(false); props.onMaster(); }}><span className="account-menu-icon"><Icon>{Symbols.settings}</Icon></span><span><strong>{isPlatformMaster ? 'Master OWDEX Draw' : 'Administração da empresa'}</strong><small>{isPlatformMaster ? 'Empresas, planos, usuários e auditoria' : 'Equipe, acessos e auditoria da sua empresa'}</small></span></button>}
            <button type="button" role="menuitem" className="account-signout" onClick={() => { setAccountOpen(false); props.onLogout(); }}><span className="account-menu-icon"><Icon>{Symbols.logout}</Icon></span><span><strong>Sair</strong><small>Encerrar esta sessão</small></span></button>
          </div>
        </div>}
      </div>

      <div className="toolbar-actions" role="toolbar" aria-label="Ferramentas do diagrama">
        <div className="tool-group tool-group-mobile">
          <button type="button" className="tool-button mobile-panel-button" onClick={() => props.onToggleLibrary('shapes')} title="Abrir biblioteca (Ctrl+Alt+B)">Biblioteca</button>
          <button type="button" className="tool-button mobile-panel-button" onClick={props.onToggleProperties} title="Abrir propriedades (Ctrl+Alt+P)">Propriedades</button>
        </div>
        <div className="tool-group">
          <button type="button" className={`tool-button save-now-button save-${props.saveStatus}`} onClick={props.onSave} disabled={props.readOnly || props.saveStatus === 'saving'} title={props.saveStatus === 'saving' ? 'Salvando em segundo plano…' : props.saveStatus === 'error' ? 'Falha ao salvar — tentar novamente (Ctrl+S)' : 'Salvar agora no dispositivo e sincronizar com a OWDEX (Ctrl+S)'} aria-label={props.saveStatus === 'saving' ? 'Salvando em segundo plano' : props.saveStatus === 'error' ? 'Tentar salvar novamente' : 'Salvar agora'}><Icon>{Symbols.save}</Icon><span className="save-now-label">Salvar</span><span className={`save-now-state-dot ${props.saveStatus}`} aria-hidden="true"/><span className="sr-only" aria-live="polite">{props.saveStatus === 'saving' ? 'Salvando em segundo plano' : props.saveStatus === 'error' ? 'Falha ao salvar' : 'Salvo'}</span></button>
          <button type="button" className="tool-button" onClick={props.onUndo} disabled={props.readOnly || !props.canUndo} title="Desfazer (Ctrl+Z)"><Icon>{Symbols.undo}</Icon></button>
          <button type="button" className="tool-button" onClick={props.onRedo} disabled={props.readOnly || !props.canRedo} title="Refazer (Ctrl+Y)"><Icon>{Symbols.redo}</Icon></button>
        </div>
        <div className="tool-group tool-group-primary">
          <button type="button" className={`tool-button tool-button-wide tool-accent-connect ${props.connectionMode ? 'active' : ''}`} onClick={props.onToggleConnection} disabled={props.readOnly} title="Modo de conexão. Dica: você também pode usar os pontos azuis do objeto selecionado."><Icon>{Symbols.connect}</Icon> Conexão</button>
          <button type="button" className="tool-button tool-button-wide tool-accent-library" onClick={() => props.onToggleLibrary('shapes')} title="Abrir formas da biblioteca"><Icon>{Symbols.shapes}</Icon> Formas</button><button type="button" className="tool-button tool-button-wide tool-accent-models" onClick={() => props.onToggleLibrary('templates')} title="Abrir modelos prontos diretamente"><Icon>{Symbols.template}</Icon> Modelos</button>
        </div>
        <div className="tool-group">
          <button type="button" className="tool-button tool-button-wide tool-accent-layout" onClick={() => props.onArrange('flow')} disabled={props.readOnly}><Icon>{Symbols.flow}</Icon> Fluxo</button>
          <button type="button" className="tool-button tool-button-wide tool-accent-layout" onClick={() => props.onArrange('horizontal')} disabled={props.readOnly}><Icon>{Symbols.arrange}</Icon> Horizontal</button>
          <button type="button" className="tool-button tool-button-wide tool-accent-layout" onClick={() => props.onArrange('vertical')} disabled={props.readOnly}><Icon>{Symbols.arrange}</Icon> Vertical</button>
          <select className="toolbar-select" value="" aria-label="Outros layouts automáticos" onChange={(event) => { if (event.target.value) props.onArrange(event.target.value as LayoutMode); }}><option value="">Mais layouts…</option><option value="tree">Árvore horizontal</option><option value="organization">Organograma</option><option value="mindmap">Mapa mental</option><option value="radial">Radial</option><option value="compact">Compacto</option></select>
        </div>
        <div className="tool-group">
          <select className="toolbar-select" value="" disabled={props.readOnly || props.selectedCount < 2} aria-label="Alinhar objetos" onChange={(event) => { if (event.target.value) props.onAlign(event.target.value as Parameters<Props['onAlign']>[0]); }}><option value="">Alinhar…</option><option value="left">À esquerda</option><option value="center">Ao centro</option><option value="right">À direita</option><option value="top">Ao topo</option><option value="middle">Ao meio</option><option value="bottom">À base</option></select>
          <select className="toolbar-select" value="" disabled={props.readOnly || props.selectedCount < 3} aria-label="Distribuir objetos" onChange={(event) => { if (event.target.value) props.onDistribute(event.target.value as 'horizontal' | 'vertical'); }}><option value="">Distribuir…</option><option value="horizontal">Horizontalmente</option><option value="vertical">Verticalmente</option></select>
          <select className="toolbar-select" value="" disabled={props.readOnly || props.selectedCount < 2} aria-label="Igualar tamanho" onChange={(event) => { if (event.target.value) props.onMatchSize(event.target.value as 'width' | 'height' | 'both'); }}><option value="">Mesmo tamanho…</option><option value="width">Mesma largura</option><option value="height">Mesma altura</option><option value="both">Mesmo tamanho</option></select>
        </div>
        <div className="tool-group tool-group-compact">
          <button type="button" className="tool-button" onClick={props.onGroup} disabled={props.readOnly || props.selectedCount < 2} title="Agrupar (Ctrl+G)"><Icon>{Symbols.group}</Icon></button>
          <button type="button" className="tool-button" onClick={props.onUngroup} disabled={props.readOnly || !props.canUngroup} title="Desagrupar (Ctrl+Shift+G)"><Icon>{Symbols.ungroup}</Icon></button>
          <button type="button" className="tool-button" onClick={props.onConnectSelection} disabled={props.readOnly || props.selectedCount < 2} title="Conectar objetos selecionados em sequência"><Icon>{Symbols.connect}</Icon></button>
          <button type="button" className="tool-button" onClick={props.onSelectConnectedEdges} disabled={props.selectedCount < 1} title="Selecionar todas as conexões ligadas aos objetos selecionados"><Icon>{Symbols.search}</Icon></button>
          <button type="button" className="tool-button" onClick={props.onBundleEdges} disabled={props.readOnly || !props.canBundleEdges} title="Unir conexões selecionadas em um tronco comum"><Icon>{Symbols.group}</Icon></button>
          <button type="button" className="tool-button" onClick={props.onUnbundleEdges} disabled={props.readOnly || !props.canUnbundleEdges} title="Separar conexões agrupadas"><Icon>{Symbols.ungroup}</Icon></button>
          <button type="button" className={`tool-button ${props.selectedLocked ? 'active' : ''}`} onClick={props.onToggleLock} disabled={props.readOnly || props.selectedCount === 0} title={props.selectedLocked ? 'Destravar' : 'Travar'}><Icon>{props.selectedLocked ? Symbols.unlock : Symbols.lock}</Icon></button>
          <button type="button" className="tool-button" onClick={() => props.onLayer('front')} disabled={props.readOnly || props.selectedCount === 0} title="Trazer para frente"><Icon>{Symbols.front}</Icon></button>
          <button type="button" className="tool-button" onClick={() => props.onLayer('back')} disabled={props.readOnly || props.selectedCount === 0} title="Enviar para trás"><Icon>{Symbols.back}</Icon></button>
          <button type="button" className="tool-button" onClick={props.onDuplicate} disabled={props.readOnly || props.selectedCount === 0} title="Duplicar (Ctrl+D)"><Icon>{Symbols.duplicate}</Icon></button>
          <button type="button" className="tool-button danger-tool" onClick={props.onDelete} disabled={props.readOnly || props.selectedCount === 0} title="Excluir"><Icon>{Symbols.trash}</Icon></button>
          <button type="button" className="tool-button" onClick={props.onFit} title="Ajustar ao conteúdo"><Icon>{Symbols.fit}</Icon></button>
        </div>
      </div>

      <input ref={fileRef} type="file" hidden accept=".odxd,.owdexdraw,.json,.owdexdraw.json,application/json" onChange={(event) => void handleImport(event.target.files?.[0])} />
      <input ref={orgCsvRef} type="file" hidden accept=".csv,text/csv,text/plain" onChange={(event) => void handleOrgCsv(event.target.files?.[0])} />
      <input ref={sqlRef} type="file" hidden accept=".sql,text/plain,application/sql" onChange={(event) => void handleSql(event.target.files?.[0])} />
      <input ref={networkCsvRef} type="file" hidden accept=".csv,text/csv,text/plain" onChange={(event) => void handleNetworkCsv(event.target.files?.[0])} />
      <input ref={mermaidRef} type="file" hidden accept=".mmd,.mermaid,text/plain" onChange={(event) => void handleMermaid(event.target.files?.[0])} />
      <input ref={dataCsvRef} type="file" hidden accept=".csv,text/csv,text/plain" onChange={(event) => void handleDataCsv(event.target.files?.[0])} />
      <input ref={plantUmlRef} type="file" hidden accept=".puml,.plantuml,.txt,text/plain" onChange={(event) => void handlePlantUml(event.target.files?.[0])} />
      <input ref={dotRef} type="file" hidden accept=".dot,.gv,text/plain,text/vnd.graphviz" onChange={(event) => void handleDot(event.target.files?.[0])} />
      <input ref={bpmnXmlRef} type="file" hidden accept=".bpmn,.xml,application/xml,text/xml" onChange={(event) => void handleBpmnXml(event.target.files?.[0])} />
      <input ref={cloudCsvRef} type="file" hidden accept=".csv,text/csv,text/plain" onChange={(event) => void handleCloudCsv(event.target.files?.[0])} />
      <input ref={workspaceBackupRef} type="file" hidden accept=".json,application/json" onChange={(event) => void handleWorkspaceBackup(event.target.files?.[0])} />
    </header>
  );
}
